import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Grid,
  Typography,
  styled,
  Link,
  SimpleModal,
} from '@nextiva/next-ui-components';
import { useQuery } from 'react-query';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { getLocksForAllEntities } from 'services';
import { BodyGrid } from './DataImport.styles';
import ImportTile from './ImportTile';
import { screens } from '.';

interface Props {
  setStartImport: Dispatch<SetStateAction<string>>;
}

const Text = styled(Typography)`
  margin-top: 20px;
`;

const DataImportTiles = ({ setStartImport }: Props) => {
  const {
    featureState: { bulkImportsLocations, bulkUsersEdit, bulkImportsTeams },
    corpAcctNbr,
  } = useContext(FeatureContext);
  const { refetch: refetchJobLocks } = useQuery('jobLocks', () =>
    getLocksForAllEntities(corpAcctNbr),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const { pendo, location } = window;
    pendo?.location?.setUrl(`${location.href}/imports`);
    void refetchJobLocks();
    return () => pendo?.location?.useBrowserUrl();
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Import company data</Typography>
      </Grid>
      <Grid item xs={10}>
        <Text variant="body1">
          {bulkUsersEdit ? (
            <>
              Quickly import and modify your company&apos;s data in bulk. Begin
              the import process by selecting a data import type below to import
              new data {'\n'}or edit an existing template that can be
              re-imported. Learn more about
              <Link
                variant="body1"
                href="/"
                onClick={openModal}
                data-testid="PlatformAdminUI-DataImport-FileCompatibility-Button"
                sx={{
                  margin: '0 0 0 8px',
                  textDecoration: 'underline',
                }}
              >
                file compatibility
              </Link>
              .
            </>
          ) : (
            <>
              To use our import feature, please ensure you download and utilize
              one of our specified templates.{' '}
              <b>It is required for importing</b> and ensuring compatibility.
            </>
          )}
        </Text>
      </Grid>
      <BodyGrid container item xs={12} flexDirection="row">
        {bulkImportsLocations && (
          <ImportTile
            title="Locations"
            clickHandler={() => setStartImport(screens.LOCATIONS_IMPORT)}
          />
        )}
        <ImportTile
          title="Phones & Devices"
          clickHandler={() => setStartImport(screens.PHONES_AND_DEVICES_IMPORT)}
        />
        {bulkImportsTeams && (
          <ImportTile
            title="Teams"
            clickHandler={() => setStartImport(screens.TEAMS_IMPORT)}
          />
        )}
        <ImportTile
          title="Users"
          clickHandler={() => setStartImport(screens.USERS_IMPORT)}
          setStartImport={setStartImport}
        />
      </BodyGrid>
      <SimpleModal
        title="File compatibility"
        confirmButtonProps={{
          onClick: closeModal,
          'data-testid':
            'PlatformAdminUI-DataImport-FileCompatibility-SimpleModal-CloseButton',
        }}
        confirmText="Close"
        open={isModalOpen}
        onClose={closeModal}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            data-testid="PlatformAdminUI-DataImport-FileCompatibility-Context"
            style={{ marginBottom: '16px' }}
          >
            <Typography variant="body1">
              For the best performance and compatibility, we advise using
              Microsoft Excel&apos;s native application. Other apps like Google
              Sheets may result in format and data discrepancies, potentially
              leading to import failures.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            data-testid="PlatformAdminUI-DataImport-FileCompatibility-Modal-RecommendedApp"
            style={{ marginBottom: '16px' }}
          >
            <Typography variant="body1" fontWeight="bold">
              Recommended application
            </Typography>
            <Typography variant="body1">Microsoft Excel</Typography>
          </Grid>
          <Grid
            item
            xs={12}
            data-testid="PlatformAdminUI-DataImport-FileCompatibility-Modal-SupportedFormat"
          >
            <Typography variant="body1" fontWeight="bold">
              Supported format
            </Typography>
            <Typography variant="body1">XLSX</Typography>
          </Grid>
        </Grid>
      </SimpleModal>
    </Grid>
  );
};

export default DataImportTiles;
